/**
 * Utility functions for handling day name inconsistencies in the application.
 * The backend uses lowercase English day names (monday, tuesday, etc.) but 
 * Sunday is defined as 'sun' instead of 'sunday'. This file provides functions
 * to normalize day names across the application.
 */

// Map of day names in different formats
const dayMappings = {
  // English lowercase
  'monday': 'monday',
  'tuesday': 'tuesday',
  'wednesday': 'wednesday',
  'thursday': 'thursday',
  'friday': 'friday',
  'saturday': 'saturday',
  'sunday': 'sun', // Special case: backend uses 'sun' instead of 'sunday'
  'sun': 'sun',
  
  // French lowercase
  'lundi': 'monday',
  'mardi': 'tuesday',
  'mercredi': 'wednesday',
  'jeudi': 'thursday',
  'vendredi': 'friday',
  'samedi': 'saturday',
  'dimanche': 'sun', // Maps to 'sun' for backend consistency
  
  // Capitalized French (as used in UI)
  'Lundi': 'monday',
  'Mardi': 'tuesday',
  'Mercredi': 'wednesday',
  'Jeudi': 'thursday',
  'Vendredi': 'friday',
  'Samedi': 'saturday',
  'Dimanche': 'sun', // Maps to 'sun' for backend consistency
};

// Display names for days (French)
const displayNames = {
  'monday': 'Lundi',
  'tuesday': 'Mardi',
  'wednesday': 'Mercredi',
  'thursday': 'Jeudi',
  'friday': 'Vendredi',
  'saturday': 'Samedi',
  'sun': 'Dimanche', // Special case for 'sun'
  'sunday': 'Dimanche',
};

/**
 * Normalizes a day name to the backend format
 * @param {string} dayName - Day name in any supported format
 * @returns {string} - Normalized day name in backend format
 */
export const normalizeDayName = (dayName) => {
  if (!dayName) return '';
  
  const lowerCaseDayName = dayName.toLowerCase();
  return dayMappings[lowerCaseDayName] || lowerCaseDayName;
};

/**
 * Compares two day names to check if they refer to the same day
 * @param {string} day1 - First day name
 * @param {string} day2 - Second day name
 * @returns {boolean} - True if both names refer to the same day
 */
export const isSameDay = (day1, day2) => {
  if (!day1 || !day2) return false;
  
  const normalizedDay1 = normalizeDayName(day1);
  const normalizedDay2 = normalizeDayName(day2);
  
  return normalizedDay1 === normalizedDay2;
};

/**
 * Gets the display name for a day
 * @param {string} dayName - Day name in any supported format
 * @returns {string} - Display name for the day (capitalized in French)
 */
export const getDisplayDay = (dayName) => {
  if (!dayName) return '';
  
  const normalizedDay = normalizeDayName(dayName);
  return displayNames[normalizedDay] || dayName;
};

/**
 * Gets the backend format for a day name
 * @param {string} dayName - Day name in any supported format
 * @returns {string} - Day name in backend format
 */
export const getBackendDayFormat = (dayName) => {
  return normalizeDayName(dayName);
};

export default {
  normalizeDayName,
  isSameDay,
  getDisplayDay,
  getBackendDayFormat
};
